import { useCallback, useRef } from 'react';

import { useFetchWerklijnenOpId } from '@/components/search/hooks/useFetchWerklijnenOpId';
import useClientState from '@/state/useClientState';
import { isEqualTo } from '@/utils/helpers';

import { ApiResourceKey } from '../types/types';
import { werklijnComparator } from '../utils/werklijnen';

/**
 * Deze functie ververst alle werklijnen in de store.
 *
 * Op dit moment wordt dit gebruikt voor het updaten van alle gegevens i.v.m. UM's,
 * aangezien daar nog geen event-source voor beschikbaar is.
 *
 * Daarnaast is dit ook de fallback voor als SSE onverhoopt niet werkt
 *
 * @returns een object met daarin de functie `verversAlleWerklijnen`
 */
const useVerversAlleWerklijnen = () => {
  const huidigeWerklijnIds = useClientState((state) => state.huidigeWerklijnIds);
  const vervangAlleWerklijnenMet = useClientState((state) => state.vervangAlleWerklijnen);
  const favorieteWerklijnen = useClientState((state) => state.naarBovenGeplaatsteWerklijnen);
  const werklijnSortering = useClientState((state) => state.werklijnSortering);
  const handleApiError = useClientState(
    (state) => (queryKey: ApiResourceKey, isError: boolean) =>
      isError ? state.showApiError(queryKey) : state.resetApiError(queryKey),
  );
  const favoriteIndexMap = new Map<string, number>();
  favorieteWerklijnen.forEach((id, index) => {
    favoriteIndexMap.set(id, index + 1);
  });

  const { fetchWerklijnenOpIds } = useFetchWerklijnenOpId();
  const previousFavorieteWerklijnenLength = useRef(0);
  const verversAlleWerklijnen = useCallback(() => {
    (async () => {
      const teRefreshenIds = huidigeWerklijnIds();
      // als de favorietenArray met 1 is toegenomen of 1 is afgenomen, dan hoeven we niet te refreshen
      if (
        favorieteWerklijnen.length === previousFavorieteWerklijnenLength.current + 1 ||
        favorieteWerklijnen.length === previousFavorieteWerklijnenLength.current - 1
      ) {
        // update de vorige lengte van de array
        previousFavorieteWerklijnenLength.current = favorieteWerklijnen.length;
        return;
      }
      if (teRefreshenIds.length) {
        try {
          const werklijnenUitDatabase = await fetchWerklijnenOpIds(teRefreshenIds);
          // Om te voorkomen dat bij zoeken soms de nieuwe werklijnen weer verdwijnen uit het WMD
          // checken we na het ophalen even of de lijst van IDs nog afdoende is:
          if (isEqualTo(huidigeWerklijnIds(), teRefreshenIds)) {
            // Als er favoriete werklijnen zijn, zet deze bovenaan
            const werklijnenUitDatabaseMetFavorieten = werklijnenUitDatabase
              .map((werklijn) => {
                if (favorieteWerklijnen.includes(werklijn.id)) {
                  werklijn.isFavorite = true;
                  werklijn.favoriteIndex = favoriteIndexMap.get(werklijn.id) ?? 0;
                  werklijn.isLastFavorite = favorieteWerklijnen?.[0].includes(werklijn.id);
                }
                return werklijn;
              })
              .sort((a, b) => werklijnComparator(a, b, werklijnSortering));
            vervangAlleWerklijnenMet(werklijnenUitDatabaseMetFavorieten);
          }
          handleApiError('verversWerklijnen', false);
        } catch (error) {
          console.error(error);
          handleApiError('verversWerklijnen', true);
        }
      }
    })();
  }, [favorieteWerklijnen, fetchWerklijnenOpIds, huidigeWerklijnIds, vervangAlleWerklijnenMet]);

  return { verversAlleWerklijnen };
};
export default useVerversAlleWerklijnen;
